import { useState } from 'react';
import { TimesheetRowType } from '../types/timesheet';
import {
  Grid,
  TextField,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Tooltip,
  Badge,
} from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

type TimesheetRowProps = {
  row: TimesheetRowType;
  rowIndex: number;
  updateRowField: (
    rowIndex: number,
    field: keyof TimesheetRowType,
    value: any
  ) => void;
  updateHours: (rowIndex: number, dayIndex: number, value: number) => void;
  removeRow?: (rowIndex: number) => void;
  showRemove: boolean;
  disabled?: boolean;
  weekDates?: string[];
  weekDays?: string[];
  isMobile?: boolean;
};

export default function TimesheetRow({
  row,
  rowIndex,
  updateRowField,
  updateHours,
  removeRow,
  showRemove,
  disabled,
  weekDates = [],
  weekDays = [],
  isMobile = false,
}: TimesheetRowProps) {
  const rowTotal = row.hours.reduce((sum, h) => sum + h, 0);
  const [showNotes, setShowNotes] = useState(!!row.notes);
  const hasNotes = !!row.notes && row.notes.trim().length > 0;

  // Mobile card layout
  if (isMobile) {
    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Task/Notes inputs */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TextField
                placeholder="Task description"
                value={row.task}
                onChange={(e) =>
                  updateRowField(rowIndex, 'task', e.target.value)
                }
                size="small"
                disabled={disabled}
                fullWidth
              />
              <Tooltip title={hasNotes ? 'Edit notes' : 'Add notes'}>
                <IconButton
                  size="small"
                  onClick={() => setShowNotes(!showNotes)}
                  sx={{
                    color: hasNotes ? 'primary.main' : 'text.disabled',
                  }}
                >
                  <Badge variant="dot" color="primary" invisible={!hasNotes}>
                    {showNotes ? (
                      <ExpandLessIcon />
                    ) : (
                      <CommentIcon fontSize="small" />
                    )}
                  </Badge>
                </IconButton>
              </Tooltip>
              {showRemove && removeRow && (
                <Tooltip title="Remove task">
                  <IconButton
                    size="small"
                    onClick={() => removeRow(rowIndex)}
                    sx={{ color: 'error.main' }}
                    disabled={disabled}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Collapse in={showNotes}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: (theme) => theme.palette.custom.softBackground,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  mb: 1,
                }}
              >
                <TextField
                  placeholder="Add notes about this task..."
                  value={row.notes}
                  onChange={(e) =>
                    updateRowField(rowIndex, 'notes', e.target.value)
                  }
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  disabled={disabled}
                  sx={{ '& .MuiInputBase-root': { bgcolor: 'white' } }}
                />
              </Box>
            </Collapse>
          </Box>

          {/* Hours grid - 2 columns on mobile */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1.5,
              mb: 2,
            }}
          >
            {row.hours.map((h, dayIndex) => (
              <Box key={dayIndex}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  {weekDates[dayIndex]} ({weekDays[dayIndex]})
                </Typography>
                <TextField
                  type="number"
                  inputProps={{
                    min: 0,
                    max: 24,
                    step: 0.25,
                    style: { textAlign: 'center' },
                    inputMode: 'decimal',
                  }}
                  value={h === 0 ? '' : h}
                  onChange={(e) => {
                    const val =
                      e.target.value === '' ? 0 : parseFloat(e.target.value);
                    if (e.target.value === '' || !isNaN(val)) {
                      updateHours(rowIndex, dayIndex, val);
                    }
                  }}
                  size="small"
                  disabled={disabled}
                  fullWidth
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 48, // Larger touch target
                    },
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* Total */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pt: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Week Total
            </Typography>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {rowTotal.toFixed(2)} hrs
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Desktop row layout
  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={3}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <TextField
              placeholder="Task description"
              value={row.task}
              onChange={(e) => updateRowField(rowIndex, 'task', e.target.value)}
              size="small"
              disabled={disabled}
              fullWidth
            />
            <Tooltip title={hasNotes ? 'Edit notes' : 'Add notes'}>
              <IconButton
                size="small"
                onClick={() => setShowNotes(!showNotes)}
                sx={{
                  color: hasNotes ? 'primary.main' : 'text.disabled',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <Badge variant="dot" color="primary" invisible={!hasNotes}>
                  {showNotes ? (
                    <ExpandLessIcon />
                  ) : (
                    <CommentIcon fontSize="small" />
                  )}
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
        {row.hours.map((h, dayIndex) => (
          <Grid item xs={1} key={dayIndex} sx={{ textAlign: 'center' }}>
            <TextField
              type="number"
              inputProps={{
                min: 0,
                max: 24,
                step: 0.25,
                style: { textAlign: 'center' },
              }}
              value={h === 0 ? '' : h}
              onChange={(e) => {
                const val =
                  e.target.value === '' ? 0 : parseFloat(e.target.value);
                if (e.target.value === '' || !isNaN(val)) {
                  updateHours(rowIndex, dayIndex, val);
                }
              }}
              size="small"
              disabled={disabled}
            />
          </Grid>
        ))}
        <Grid item xs={1} sx={{ textAlign: 'center', pt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {rowTotal.toFixed(2)}
          </Typography>
        </Grid>
        <Grid item xs={1} sx={{ textAlign: 'center' }}>
          {showRemove && removeRow && (
            <Tooltip title="Remove task">
              <IconButton
                size="small"
                onClick={() => removeRow(rowIndex)}
                sx={{ color: 'error.main' }}
                disabled={disabled}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Grid>
      </Grid>

      {/* Collapsible Notes Section */}
      <Collapse in={showNotes}>
        <Box
          sx={{
            mt: 1,
            ml: 0,
            p: 1.5,
            bgcolor: (theme) => theme.palette.custom.softBackground,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CommentIcon fontSize="small" color="primary" />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              Notes & Comments
            </Typography>
          </Box>
          <TextField
            placeholder="Add notes about this task... (e.g., project details, client feedback, issues encountered)"
            value={row.notes}
            onChange={(e) => updateRowField(rowIndex, 'notes', e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
            disabled={disabled}
            sx={{
              '& .MuiInputBase-root': {
                bgcolor: (theme) => theme.palette.custom.inputBackground,
              },
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
